import type { ChatHistory, Message } from 'types/chatbot';
import { isRecord } from './typeChecks';

const DEFAULT_CHATBOT_SIZE = { w: 600, h: 700 };

type MigrationStorage = Pick<Storage, 'setItem' | 'removeItem'>;

const parseJsonRecord = (value: string): Record<string, unknown> | undefined => {
  try {
    const parsed: unknown = JSON.parse(value);

    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const isMessage = (value: unknown): value is Message =>
  isRecord(value) && (value.role === 'user' || value.role === 'assistant') && typeof value.content === 'string';

const toChatHistory = (value: unknown): ChatHistory | undefined => {
  if (!isRecord(value) || typeof value.id !== 'string' || !Array.isArray(value.messages) || !value.messages.every(isMessage)) return undefined;
  if (typeof value.timestamp !== 'number') return undefined;

  let assistantId: string | null;
  if (value.assistantId === null || typeof value.assistantId === 'string') {
    assistantId = value.assistantId;
  } else if (!('assistantId' in value) && typeof value.mode === 'string') {
    assistantId = null;
  } else {
    return undefined;
  }

  if (typeof value.scope !== 'string' || value.scope === '' || value.scope.startsWith('legacy:unscoped:')) return undefined;

  return { id: value.id, scope: value.scope, messages: value.messages, assistantId, timestamp: value.timestamp };
};

const parseStandaloneHistory = (value: string | null): ChatHistory[] | undefined => {
  if (!value) return undefined;

  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(toChatHistory).filter((item): item is ChatHistory => item !== undefined) : undefined;
  } catch {
    return undefined;
  }
};

const parseStandaloneSize = (value: string | null): { w: number; h: number } => {
  if (!value) return DEFAULT_CHATBOT_SIZE;

  try {
    const parsed: unknown = JSON.parse(value);
    if (isRecord(parsed) && typeof parsed.w === 'number' && typeof parsed.h === 'number') return { w: parsed.w, h: parsed.h };
  } catch {
    return DEFAULT_CHATBOT_SIZE;
  }

  return DEFAULT_CHATBOT_SIZE;
};

export const migrateStandaloneChatboxPersistence = (
  sizeValue: string | null,
  historyValue: string | null,
  openValue: string | null
): string | undefined => {
  if (!sizeValue && !historyValue && !openValue) return undefined;

  const open = (() => {
    try {
      return openValue ? JSON.parse(openValue) === true : false;
    } catch {
      return false;
    }
  })();

  return JSON.stringify({
    chatbot: JSON.stringify({
      open,
      fullScreen: false,
      size: parseStandaloneSize(sizeValue),
      activeHistoryId: null,
      history: parseStandaloneHistory(historyValue) ?? [],
    }),
  });
};

export const migrateChatbotPersistence = (persistedValue: string): string | undefined => {
  const persistedState = parseJsonRecord(persistedValue);
  if (!persistedState || typeof persistedState.chatbot !== 'string') return undefined;

  const chatbotState = parseJsonRecord(persistedState.chatbot);
  if (!chatbotState || !Array.isArray(chatbotState.history)) return undefined;

  const history = chatbotState.history.map(toChatHistory).filter((item): item is ChatHistory => item !== undefined);

  return JSON.stringify({ chatbot: JSON.stringify({ ...chatbotState, activeHistoryId: null, history }) });
};

export const persistMigratedChatbotState = (storage: MigrationStorage, key: string, legacyKey: string, value: string): boolean => {
  try {
    storage.setItem(key, value);
    storage.removeItem(legacyKey);
    return true;
  } catch {
    return false;
  }
};
